export const RESOURCES = {
  PROJECT: 'project',
  TASK: 'task',
  COMMENT: 'comment',
  REPORT: 'report',
  FOLDER: 'folder',
  QUOTATION: 'quotation',
  ORDERS: 'orders',
  SPACE: 'space',
  AI_ANALYSER: 'ai-analyser',
};

export const ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  SHARE: 'share',
  MOVE: 'move',
  DOWNLOAD: 'download',
  BILLING: 'billing',
  INVITE_ADMIN: 'invite_admin',
  INVITE_MANAGER: 'invite_manager',
  INVITE_MEMBER: 'invite_member',
  UPDATE_STATUS: 'update_status',
  REORDER_COLUMNS: 'reorder_columns',
  SORT_COLUMNS: 'sort_columns',
  REORDER_ROWS: 'reorder_rows',
  AI_ANALYSER: 'ai-analyser',
};

export const ROLES = {
  ANONYMOUS: 'anonymous',
  MEMBER: 'member',
  MANAGER: 'manager',
  ADMIN: 'admin',
  OWNER: 'owner',
};

export const SPACES = {
  PERSONAL: 'personal',
  ORGANIZATION: 'organization',
};

const commonPermissions = {
  [ACTIONS.READ]: true,
  [ACTIONS.CREATE]: true,
  [ACTIONS.UPDATE]: true,
  [ACTIONS.DELETE]: true,
  [ACTIONS.SHARE]: true,
  [ACTIONS.DOWNLOAD]: true,
};

const isOwner = (user, resource) => user?.uid === resource?.uid;
const isCommentsEnabled = context =>
  context?.commentsEnabled === true || context?.commentsEnabled === 1;
const isAssignee = (user, resource) => {
  return resource?.assignees?.some(assignee => assignee?.uid === user?.uid);
};
const isInSameOrg = (user, resource) =>
  user?.selectedOrg === resource?.organizationId;

export const PERSONAL_SPACE_PERMISSIONS = {
  ADMIN: {
    [RESOURCES.SPACE]: {
      ...commonPermissions,
      [ACTIONS.REORDER_COLUMNS]: true,
      [ACTIONS.SORT_COLUMNS]: true,
      [ACTIONS.REORDER_ROWS]: true,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.TASK]: {
      ...commonPermissions,
      [ACTIONS.MOVE]: true,
      [ACTIONS.INVITE_MEMBER]: true,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.PROJECT]: {
      ...commonPermissions,
      [ACTIONS.INVITE_MEMBER]: true,
    },
    [RESOURCES.COMMENT]: {
      ...commonPermissions,
      [ACTIONS.UPDATE]: (u, r) => isOwner(u, r),
      [ACTIONS.DELETE]: (u, r) => isOwner(u, r),
      [ACTIONS.UPDATE_STATUS]: true,
    },
    [RESOURCES.QUOTATION]: { ...commonPermissions },
    [RESOURCES.AI_ANALYSER]: { [ACTIONS.AI_ANALYSER]: true },
    [RESOURCES.ORDERS]: {
      [ACTIONS.READ]: true,
      [ACTIONS.UPDATE]: true,
    },
  },
  MEMBER: {
    [RESOURCES.SPACE]: {
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.PROJECT]: { [ACTIONS.READ]: true },
    [RESOURCES.TASK]: {
      [ACTIONS.READ]: true,
      [ACTIONS.CREATE]: (u, r, c) => c.parentFolder,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.COMMENT]: {
      [ACTIONS.CREATE]: (u, r, c) => isCommentsEnabled(c),
      [ACTIONS.READ]: true,
      [ACTIONS.UPDATE]: (u, r) => isOwner(u, r),
      [ACTIONS.DELETE]: (u, r) => isOwner(u, r),
    },
    [RESOURCES.QUOTATION]: {
      [ACTIONS.READ]: true,
    },
    [RESOURCES.AI_ANALYSER]: { [ACTIONS.AI_ANALYSER]: true },
  },
  ANONYMOUS: {
    [RESOURCES.COMMENT]: { [ACTIONS.READ]: true },
    [RESOURCES.TASK]: { [ACTIONS.SHARE]: true },
  },
};

export const ORG_PERMISSION_RULES = {
  [ROLES.ANONYMOUS]: {
    [RESOURCES.COMMENT]: { [ACTIONS.READ]: true },
    [RESOURCES.QUOTATION]: { [ACTIONS.READ]: true },
    [RESOURCES.TASK]: { [ACTIONS.SHARE]: true },
  },
  [ROLES.MEMBER]: {
    [RESOURCES.SPACE]: {
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.PROJECT]: { [ACTIONS.READ]: isInSameOrg },
    [RESOURCES.TASK]: {
      ...commonPermissions,
      // can upload to non-root projects if invited (already filtered by backend)
      [ACTIONS.CREATE]: (u, r, c) => c.parentFolder,
      [ACTIONS.UPDATE]: (u, r) => isOwner(u, r),
      [ACTIONS.DELETE]: (u, r) => isOwner(u, r),
      [ACTIONS.MOVE]: (u, r) => isOwner(u, r),
      [ACTIONS.INVITE_MEMBER]: true,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.COMMENT]: {
      [ACTIONS.CREATE]: (u, r, c) => isCommentsEnabled(c),
      [ACTIONS.READ]: true,
      [ACTIONS.UPDATE]: (u, r) => isOwner(u, r),
      [ACTIONS.DELETE]: (u, r) => isOwner(u, r),
      [ACTIONS.UPDATE_STATUS]: (u, r, c) => isAssignee(u, r, c),
    },
    [RESOURCES.REPORT]: { ...commonPermissions },
    [RESOURCES.QUOTATION]: { [ACTIONS.READ]: true },
    [RESOURCES.AI_ANALYSER]: {
      [ACTIONS.AI_ANALYSER]: (u, r) => isInSameOrg(u, r) || isOwner(u, r),
    },
  },
  [ROLES.MANAGER]: {
    [RESOURCES.SPACE]: {
      ...commonPermissions,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.PROJECT]: {
      ...commonPermissions,
      [ACTIONS.INVITE_MANAGER]: true,
      [ACTIONS.INVITE_MEMBER]: true,
    },
    [RESOURCES.TASK]: {
      ...commonPermissions,
      [ACTIONS.MOVE]: true,
      [ACTIONS.INVITE_MANAGER]: true,
      [ACTIONS.INVITE_MEMBER]: true,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.COMMENT]: {
      ...commonPermissions,
      [ACTIONS.UPDATE]: (u, r) => isOwner(u, r),
    },
    [RESOURCES.FOLDER]: { ...commonPermissions },
    [RESOURCES.REPORT]: { ...commonPermissions },
    [RESOURCES.QUOTATION]: { ...commonPermissions },
    [RESOURCES.AI_ANALYSER]: { [ACTIONS.AI_ANALYSER]: true },
    [RESOURCES.ORDERS]: {
      [ACTIONS.READ]: true,
      [ACTIONS.UPDATE]: true,
      [ACTIONS.UPDATE_STATUS]: true,
    },
  },
  [ROLES.ADMIN]: {
    [RESOURCES.SPACE]: {
      ...commonPermissions,
      [ACTIONS.BILLING]: true,
      [ACTIONS.REORDER_COLUMNS]: true,
      [ACTIONS.SORT_COLUMNS]: true,
      [ACTIONS.REORDER_ROWS]: true,
      [ACTIONS.AI_ANALYSER]: true,
      [ACTIONS.INVITE_ADMIN]: true,
      [ACTIONS.INVITE_MANAGER]: true,
      [ACTIONS.INVITE_MEMBER]: true,
    },
    [RESOURCES.PROJECT]: {
      ...commonPermissions,
      [ACTIONS.INVITE_ADMIN]: true,
      [ACTIONS.INVITE_MANAGER]: true,
      [ACTIONS.INVITE_MEMBER]: true,
    },
    [RESOURCES.TASK]: {
      ...commonPermissions,
      [ACTIONS.MOVE]: true,
      [ACTIONS.INVITE_ADMIN]: true,
      [ACTIONS.INVITE_MANAGER]: true,
      [ACTIONS.INVITE_MEMBER]: true,
      [ACTIONS.AI_ANALYSER]: true,
    },
    [RESOURCES.COMMENT]: {
      ...commonPermissions,
      [ACTIONS.UPDATE]: (u, r) => isOwner(u, r),
      [ACTIONS.UPDATE_STATUS]: true,
    },
    [RESOURCES.FOLDER]: { ...commonPermissions },
    [RESOURCES.REPORT]: { ...commonPermissions },
    [RESOURCES.QUOTATION]: { ...commonPermissions },
    [RESOURCES.AI_ANALYSER]: { [ACTIONS.AI_ANALYSER]: true },
    [RESOURCES.ORDERS]: {
      [ACTIONS.READ]: true,
      [ACTIONS.UPDATE]: true,
    },
  },
};
